/**
 * Limpeza de dados locais em logout, saída da viagem e troca de conta.
 *
 * A contrapartida da decisão de NÃO cachear resposta autenticada no Service
 * Worker: como os dados da viagem vivem no IndexedDB e no TanStack Query,
 * eles precisam de uma limpeza dirigida — e ela não pode ser silenciosa nem
 * parcial.
 *
 * Regra que atravessa toda a função: **nada de mutação pendente é descartado
 * sem que a pessoa saiba**. Se o outbox tiver escritas não sincronizadas, a
 * limpeza é recusada e o chamador confirma antes.
 */

export type ClearScope =
  /** Sair da conta. Apaga tudo que é do usuário. */
  | 'logout'
  /** Sair de uma viagem, continuando logado. Apaga só aquela viagem. */
  | { leaveTrip: string }
  /** Trocar de conta no mesmo aparelho. Tão destrutivo quanto logout. */
  | 'switch_account';

export interface ClearResult {
  cleared: boolean;
  /** Preenchido quando havia trabalho não sincronizado. */
  blockedByPending?: { count: number };
}

export interface ClearDeps {
  /** Quantas mutações ainda não foram para o servidor. */
  countPendingMutations: () => Promise<number>;
  clearOutbox: () => Promise<void>;
  clearLocalCache: () => Promise<void>;
  /** Cache em memória do TanStack Query. */
  clearQueryCache: () => void;
  /** Manda o SW apagar os caches de sessão. */
  clearServiceWorkerCaches: () => Promise<void>;
  /** Storage do navegador que carrega preferência ligada à conta. */
  clearScopedStorage: () => void;
}

/**
 * Executa a limpeza.
 *
 * `force` só deve vir de uma confirmação explícita do usuário, depois de a UI
 * dizer quantas alterações serão perdidas.
 */
export async function clearLocalData(
  scope: ClearScope,
  deps: ClearDeps,
  options: { force?: boolean } = {},
): Promise<ClearResult> {
  const pendentes = await deps.countPendingMutations();

  if (pendentes > 0 && !options.force) {
    // Apagar aqui destruiria trabalho que a pessoa acha que registrou.
    return { cleared: false, blockedByPending: { count: pendentes } };
  }

  // Ordem importa: primeiro o que é servível (caches do SW), depois o que é
  // legível (IndexedDB e memória). Se algo falhar no meio, o que sobra é
  // dado inerte, não resposta pronta para ser servida.
  await deps.clearServiceWorkerCaches();
  await deps.clearOutbox();
  await deps.clearLocalCache();
  deps.clearQueryCache();

  // Preferência de tema é do aparelho, não da conta: sobrevive ao logout.
  // O que é escopado à conta ou à viagem some.
  if (scope === 'logout' || scope === 'switch_account') {
    deps.clearScopedStorage();
  }

  return { cleared: true };
}

/** Avisa o Service Worker. Resolve mesmo sem SW — o app segue funcionando. */
export function requestServiceWorkerCacheClear(timeoutMs = 3000): Promise<void> {
  return new Promise((resolve) => {
    const controller = navigator.serviceWorker?.controller;
    if (!controller) {
      resolve();
      return;
    }

    const timer = setTimeout(finish, timeoutMs);

    function finish() {
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener('message', onMessage);
      resolve();
    }

    function onMessage(event: MessageEvent) {
      if ((event.data as { type?: string } | undefined)?.type === 'NV_SESSION_CACHES_CLEARED') {
        finish();
      }
    }

    navigator.serviceWorker.addEventListener('message', onMessage);
    controller.postMessage({ type: 'NV_CLEAR_SESSION_CACHES' });
  });
}
